import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import SaveIcon from "@mui/icons-material/Save";
import LogoutIcon from "@mui/icons-material/Logout";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/api";
import { Backdrop, CircularProgress } from "@mui/material";
import LOgo from "../../assets/Ntact.png";
import moment from "moment";
import ToastMessage from "../../utils/muitoast";

// User Information is a pre-step — it's not part of the numbered stepper below
const steps = [
  "Company Info",
  "Contacts",
  "HSE Info",
  "HSE Stats",
  "Safety",
  "Attestation",
];
const TOTAL_STEPS = steps.length; // 6

const currentYear = new Date().getFullYear();
const years = [currentYear - 1, currentYear - 2, currentYear - 3];

// Reusable input field — defined OUTSIDE the component to prevent focus loss
const InputField = ({ label, value, onChange, placeholder, required, maxLength, digitsOnly }) => (
  <div className="flex flex-col flex-1">
    <label className="text-sm font-medium mb-1 text-left">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="text"
      value={value}
      maxLength={maxLength}
      onChange={(e) => {
        let val = e.target.value;
        if (digitsOnly) val = val.replace(/\D/g, "");
        if (maxLength && val.length > maxLength) return;
        onChange(val);
      }}
      placeholder={placeholder || label}
      className="border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
    />
  </div>
);

// Radio button for Yes/No — defined OUTSIDE the component to prevent focus loss
const YesNoRadio = ({ value, onChange, noLabel }) => (
  <div className="flex gap-6 mt-1">
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="radio" checked={value === "Yes"} onChange={() => onChange("Yes")} className="accent-black" />
      Yes
    </label>
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="radio" checked={value === "No"} onChange={() => onChange("No")} className="accent-black" />
      {noLabel || "No"}
    </label>
  </div>
);

// File attachment component
const FileAttachment = ({ file, onChange, onRemove }) => (
  <div className="mt-2">
    {!file ? (
      <label className="inline-flex items-center gap-2 text-sm text-gray-600 border border-dashed border-gray-300 rounded-md px-4 py-2 cursor-pointer hover:bg-gray-50 transition">
        <AttachFileIcon fontSize="small" className="text-gray-500" />
        <span>Attach file</span>
        <input
          type="file"
          className="hidden"
          onChange={(e) => {
            if (e.target.files[0]) onChange(e.target.files[0]);
          }}
        />
      </label>
    ) : (
      <div className="inline-flex items-center gap-2 bg-gray-100 rounded-md px-3 py-2 text-sm">
        <AttachFileIcon fontSize="small" className="text-gray-500" />
        <span className="text-gray-700">{file.name || file}</span>
        <button onClick={onRemove} className="text-red-500 hover:text-red-700">
          <CloseIcon fontSize="small" />
        </button>
      </div>
    )}
  </div>
);

// Upload box matching the document upload design
const UploadBox = ({ file, onChange, onRemove, label }) => (
  <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
    <div className="px-4 py-2 text-sm text-gray-600 font-medium text-center border-b border-gray-200">
      {label || "Upload supporting document"}
    </div>
    {!file ? (
      <label className="flex items-center justify-center gap-2 px-4 py-3 cursor-pointer hover:bg-gray-100 transition">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" />
        </svg>
        <span className="text-sm text-gray-600">Choose files to upload</span>
        <input
          type="file"
          className="hidden"
          accept="image/*,.pdf,.doc,.docx"
          onChange={(e) => {
            if (e.target.files[0]) onChange(e.target.files[0]);
          }}
        />
      </label>
    ) : (
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <AttachFileIcon fontSize="small" className="text-gray-500" />
          <span>{file.name || file}</span>
        </div>
        <button onClick={onRemove} className="text-red-500 hover:text-red-700">
          <CloseIcon fontSize="small" />
        </button>
      </div>
    )}
  </div>
);

const SubcontractorForm = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("allow") || sessionStorage.getItem("subcontractorToken") || sessionStorage.getItem("accessToken");
  const mode = searchParams.get("mode") || "new"; // "new" | "continue"
  const [subId, setSubId] = useState(sessionStorage.getItem("subcontractorSubId") || null);
  const [loader, setLoader] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  // User Information is a pre-step shown before the numbered stepper.
  // On "new" → show it first. On "continue" → skip it (phone already verified, draft loaded).
  const [showUserInfo, setShowUserInfo] = useState(mode !== "continue");

  // Phone validation OTP states
  const [phoneVerified, setPhoneVerified] = useState(mode === "continue");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [otpSending, setOtpSending] = useState(false);
  const otpInputsRef = React.useRef([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("error");

  const showToast = (msg, severity = "error") => {
    setToastMessage(msg);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const [formData, setFormData] = useState({
    // Page 1: User Information
    firstName: "",
    lastName: "",
    phone: "",         // personal phone for OTP validation
    emailAddress: "",

    // Page 2: Company Info
    subcontractorName: "",
    telephoneNumber: "",
    streetAddress: "",
    faxNumber: "",
    city: "",
    websiteAddress: "",
    provinceState: "",
    postalCode: "",
    president: "",
    vicePresident: "",
    treasurer: "",
    servicesProvided: "",

    // Page 3: Contacts
    contacts: {
      insurance: { title: "", telephone: "", fax: "", email: "" },
      requestingBids: { title: "", telephone: "", fax: "", email: "" },
      contractorEvaluation: { title: "", telephone: "", fax: "", email: "" },
    },

    // Page 4: HSE Info
    hseNameTitle: "",
    hsePhone: "",
    hseEmail: "",

    // Page 5: HSE Stats
    hseStats: {
      totalManhours: { [years[0]]: "", [years[1]]: "", [years[2]]: "" },
      trir: { [years[0]]: "", [years[1]]: "", [years[2]]: "" },
      lwc: { [years[0]]: "", [years[1]]: "", [years[2]]: "" },
      lwdr: { [years[0]]: "", [years[1]]: "", [years[2]]: "" },
      emr: "",
      fatalities_last_3_years: "",
      serious_incidents_last_3_years: "",
    },

    // Page 6: Safety
    safetyProgram: "",
    safetyProgramAttachment: null,
    safetyManual: "",
    safetyManualAttachment: null,
    drugAlcoholTesting: "",
    onlineDatabases: {
      ISNetworld: "",
      NCMS: "",
      Avetta: "",
      Veriforce: "",
      TPS: "",
    },
    oshaCitation: "",
    oshaCitationAttachment: null,
    thirdPartyMedical: "",
    jsaProcess: "",
    rootCauseAnalysis: "",
    hseOrientation: "",
    hseOrientationAttachment: null,

    // Page 7: Attestation
    printName: "",
    position: "",
    signature: "",
    attestationDate: "",
    enclosedDocuments: [],
  });

  // Redirect to login if no token, otherwise load saved data if subId exists
  useEffect(() => {
    if (!token) {
      // navigate("/subcontractor-login");
      return;
    }
    if (mode === "continue") {
      // TODO: Backend dev will create an endpoint that returns the draft record (status "D")
      // for the verified phone number. For now we just attempt loadSavedData() if subId
      // was set, and place the user on page 1 of the stepper.
      loadDraftForContinue();
    } else if (subId) {
      loadSavedData();
    }
  }, []);

  const loadDraftForContinue = async () => {
    const savedId = sessionStorage.getItem("subcontractorSubId");
    if (savedId) {
      setSubId(savedId);
      await loadSavedData(savedId);
    }
    setShowUserInfo(false);
    setCurrentPage(1);
  };

  const loadSavedData = async (id) => {
    const useId = id || subId;
    if (!useId) return;
    setLoader(true);
    try {
      const response = await api.get(`/v1/subcontractor/get?id=${useId}`);
      if (response.status === 200 && response.data) {
        const saved = response.data;
        setFormData((prev) => {
          const updated = { ...prev };

          // Load User Info from firstname/lastname/phone/email columns
          updated.firstName = saved.firstname || prev.firstName || "";
          updated.lastName = saved.lastname || prev.lastName || "";
          updated.phone = saved.phone || prev.phone || "";
          updated.emailAddress = saved.email || prev.emailAddress || "";

          // Load Company Info
          if (saved.companyinfo) {
            const ci = saved.companyinfo;
            updated.subcontractorName = ci.subcontractorName || "";
            updated.telephoneNumber = ci.telephoneNumber || "";
            updated.streetAddress = ci.streetAddress || "";
            updated.faxNumber = ci.faxNumber || "";
            updated.city = ci.city || "";
            updated.websiteAddress = ci.websiteAddress || "";
            updated.provinceState = ci.provinceState || "";
            updated.postalCode = ci.postalCode || "";
            updated.president = ci.president || "";
            updated.vicePresident = ci.vicePresident || "";
            updated.treasurer = ci.treasurer || "";
            updated.servicesProvided = ci.servicesProvided || "";
          }

          // Load Contacts
          if (saved.contacts) {
            updated.contacts = {
              insurance: saved.contacts.insurance || prev.contacts.insurance,
              requestingBids: saved.contacts.requestingBids || prev.contacts.requestingBids,
              contractorEvaluation: saved.contacts.contractorEvaluation || prev.contacts.contractorEvaluation,
            };
          }

          // Load HSE Info
          if (saved.hseinfo) {
            updated.hseNameTitle = saved.hseinfo.hseNameTitle || "";
            updated.hsePhone = saved.hseinfo.hsePhone || "";
            updated.hseEmail = saved.hseinfo.hseEmail || "";
          }

          // Load HSE Stats
          if (saved.hsestats) {
            updated.hseStats = saved.hsestats;
          }

          // Load Safety
          if (saved.safety) {
            const s = saved.safety;
            updated.safetyProgram = s.safetyProgram || "";
            updated.safetyManual = s.safetyManual || "";
            updated.drugAlcoholTesting = s.drugAlcoholTesting || "";
            updated.onlineDatabases = s.onlineDatabases || prev.onlineDatabases;
            updated.oshaCitation = s.oshaCitation || "";
            updated.thirdPartyMedical = s.thirdPartyMedical || "";
            updated.jsaProcess = s.jsaProcess || "";
            updated.rootCauseAnalysis = s.rootCauseAnalysis || "";
            updated.hseOrientation = s.hseOrientation || "";
          }

          // Load Attestation
          if (saved.attestation) {
            updated.printName = saved.attestation.printName || "";
            updated.position = saved.attestation.position || "";
            updated.signature = saved.attestation.signature || "";
            updated.attestationDate = saved.attestation.attestationDate || "";
            updated.enclosedDocuments = saved.attestation.enclosedDocuments || [];
          }

          return updated;
        });
        setDataLoaded(true);
      }
    } catch (err) {
      console.error("Error loading saved data:", err);
    } finally {
      setLoader(false);
    }
  };

  const buildPayload = (status = "draft") => {
    const payload = {
      firstname: formData.firstName,
      lastname: formData.lastName,
      phone: formData.phone,
      email: formData.emailAddress,
      companyinfo: {
        subcontractorName: formData.subcontractorName,
        telephoneNumber: formData.telephoneNumber,
        streetAddress: formData.streetAddress,
        faxNumber: formData.faxNumber,
        city: formData.city,
        websiteAddress: formData.websiteAddress,
        provinceState: formData.provinceState,
        postalCode: formData.postalCode,
        president: formData.president,
        vicePresident: formData.vicePresident,
        treasurer: formData.treasurer,
        servicesProvided: formData.servicesProvided,
      },
      contacts: formData.contacts,
      hseinfo: {
        hseNameTitle: formData.hseNameTitle,
        hsePhone: formData.hsePhone,
        hseEmail: formData.hseEmail,
      },
      hsestats: formData.hseStats,
      safety: {
        safetyProgram: formData.safetyProgram,
        safetyManual: formData.safetyManual,
        drugAlcoholTesting: formData.drugAlcoholTesting,
        onlineDatabases: formData.onlineDatabases,
        oshaCitation: formData.oshaCitation,
        thirdPartyMedical: formData.thirdPartyMedical,
        jsaProcess: formData.jsaProcess,
        rootCauseAnalysis: formData.rootCauseAnalysis,
        hseOrientation: formData.hseOrientation,
      },
      attestation: {
        printName: formData.printName,
        position: formData.position,
        signature: formData.signature,
        attestationDate: formData.attestationDate,
        enclosedDocuments: formData.enclosedDocuments,
      },
      status: status,
    };
    if (subId) {
      payload.id = parseInt(subId);
    }
    return payload;
  };

  const handleSaveAndExit = async () => {
    setLoader(true);
    setSaveMessage("");
    try {
      const payload = buildPayload("D");
      const response = await api.post("/v1/subcontractor/save", payload);
      if (response.status === 200) {
        if (response.data?.id) {
          setSubId(response.data.id);
          sessionStorage.setItem("subcontractorSubId", response.data.id);
        }
        setSaveMessage("Data saved successfully!");
        setTimeout(() => navigate("/subcontractor-login"), 1500);
      } else {
        setSaveMessage("Failed to save. Please try again.");
      }
    } catch (err) {
      console.error("Save error:", err);
      setSaveMessage("Failed to save. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all pages before submit
    const allMissing = [];
    for (let p = 1; p <= TOTAL_STEPS; p++) {
      const m = validatePage(p);
      if (m.length > 0) allMissing.push(...m);
    }
    if (allMissing.length > 0) {
      showToast(`Please fill in: ${allMissing.join(", ")}`);
      return;
    }

    setLoader(true);
    setSaveMessage("");
    try {
      const payload = buildPayload("A");
      const response = await api.post("/v1/subcontractor/save", payload);
      if (response.status === 200) {
        if (response.data?.id) {
          setSubId(response.data.id);
          sessionStorage.setItem("subcontractorSubId", response.data.id);
        }
        setSaveMessage("Form submitted successfully!");
        setTimeout(() => navigate("/subcontractor-login"), 1500);
      } else {
        setSaveMessage("Submission failed. Please try again.");
      }
    } catch (err) {
      console.error("Submit error:", err);
      setSaveMessage("Submission failed. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (contactType, field, value) => {
    setFormData((prev) => ({
      ...prev,
      contacts: {
        ...prev.contacts,
        [contactType]: { ...prev.contacts[contactType], [field]: value },
      },
    }));
  };

  const handleStatChange = (stat, year, value) => {
    setFormData((prev) => ({
      ...prev,
      hseStats: {
        ...prev.hseStats,
        [stat]: { ...prev.hseStats[stat], [year]: value },
      },
    }));
  };

  const handleDatabaseChange = (db, value) => {
    setFormData((prev) => ({
      ...prev,
      onlineDatabases: { ...prev.onlineDatabases, [db]: value },
    }));
  };

  // OTP resend countdown
  React.useEffect(() => {
    if (otpResendTimer <= 0) return;
    const t = setInterval(() => setOtpResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [otpResendTimer]);

  const handleSendPhoneOtp = async () => {
    if (!formData.phone || formData.phone.replace(/\D/g, "").length < 7) {
      setSaveMessage("Please enter a valid phone number first.");
      return;
    }
    setOtpSending(true);
    setOtpError("");
    try {
      const resp = await api.post("/v1/subcontractor/send_otp", {
        phone_number: formData.phone,
        is_new: "N",
      });
      if (resp.status === 200) {
        // Store the subcontractor_id so save updates this record instead of creating new
        if (resp.subcontractor_id) {
          setSubId(resp.subcontractor_id);
          sessionStorage.setItem("subcontractorSubId", resp.subcontractor_id);
        }
        setShowOtpModal(true);
        setOtpResendTimer(59);
        setOtpDigits(["", "", "", ""]);
      }
    } catch (err) {
      setSaveMessage("Failed to send OTP. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpDigitChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otpDigits];
    next[idx] = val.slice(-1);
    setOtpDigits(next);
    if (val && idx < 3) otpInputsRef.current[idx + 1]?.focus();
  };

  const handleOtpDigitKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) {
      otpInputsRef.current[idx - 1]?.focus();
    }
  };

  const handleVerifyPhoneOtp = async () => {
    setOtpError("");
    const code = otpDigits.join("");
    if (code.length !== 4) {
      setOtpError("Please enter the full 4-digit code.");
      return;
    }
    setLoader(true);
    try {
      const resp = await api.post("/v1/subcontractor/validate_otp", {
        phone_number: formData.phone,
        otp: code,
        flag: "N",
      });
      if (resp.status === 200) {
        setPhoneVerified(true);
        setShowOtpModal(false);
        setSaveMessage("Phone verified successfully!");
        setTimeout(() => setSaveMessage(""), 3000);
      }
    } catch (err) {
      setOtpError("Invalid OTP. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  // User Info pre-step: validate then move into the numbered stepper
  const handleUserInfoNext = () => {
    if (
      !formData.firstName.trim() ||
      !formData.lastName.trim() ||
      !formData.emailAddress.trim()
    ) {
      showToast("Please fill in all required fields before proceeding.");
      return;
    }
    if (!phoneVerified) {
      showToast("Please verify your phone number before proceeding.");
      return;
    }
    setShowUserInfo(false);
    setCurrentPage(1);
  };

  const validatePage = (page) => {
    const missing = [];
    if (page === 1) {
      // Company Info — required: subcontractorName, telephoneNumber, streetAddress, city, provinceState, postalCode
      if (!formData.subcontractorName.trim()) missing.push("Subcontractor Name");
      if (!formData.telephoneNumber.trim()) missing.push("Telephone Number");
      if (!formData.streetAddress.trim()) missing.push("Street Address");
      if (!formData.city.trim()) missing.push("City");
      if (!formData.provinceState.trim()) missing.push("Province / State");
      if (!formData.postalCode.trim()) missing.push("Postal Code / Zip");
    } else if (page === 6) {
      // Attestation — required: printName, signature, attestationDate
      if (!formData.printName.trim()) missing.push("Print Name");
      if (!formData.signature.trim()) missing.push("Signature");
      if (!formData.attestationDate.trim()) missing.push("Date");
    }
    return missing;
  };

  const goToNextPage = () => {
    const missing = validatePage(currentPage);
    if (missing.length > 0) {
      showToast(`Please fill in: ${missing.join(", ")}`);
      return;
    }
    if (currentPage < TOTAL_STEPS) setCurrentPage(currentPage + 1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (mode === "new") {
      // From stepper page 1, go back to User Info pre-step
      setShowUserInfo(true);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <Backdrop open={loader} sx={{ zIndex: 9999 }}>
        <CircularProgress />
      </Backdrop>

      <div className="max-w-5xl mx-auto py-6 px-4">
        {/* Top Bar with Save & Exit */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img src={LOgo} alt="NTACT" className="h-12" />
          </div>
          <h1 className="text-lg font-bold text-center flex-1">
            Subcontractor Safety Pre-Qualification Form
          </h1>
          {!showUserInfo && (
            <button
              onClick={handleSaveAndExit}
              className="flex items-center gap-2 bg-gray-900 text-white text-sm px-4 py-2 rounded-md hover:bg-gray-800 transition"
            >
              <SaveIcon fontSize="small" />
              Save & Exit
            </button>
          )}
          {showUserInfo && <div className="w-[120px]" />}
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`text-center text-sm py-2 px-4 rounded-md mb-4 ${
            saveMessage.includes("success") ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
          }`}>
            {saveMessage}
          </div>
        )}

        {/* Document Info */}
        <div className="flex justify-between text-xs text-gray-600 mb-6 border-b pb-3">
          <div className="flex flex-col text-start">
            <div className="font-semibold text-[#000000] leading-5 text-xs font-font1">Document Name:</div>
            <div className="font-normal text-[#000000] leading-5 text-xs font-font1">Subcontractor Safety Pre-<br /> Qualification Form</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-[#000000] leading-5 text-xs font-font1">Section:</div>
            <div className="font-normal text-[#000000] leading-5 text-xs font-font1">HSE-18</div>
          </div>
          <div className="text-right">
            <div className="font-semibold text-[#000000] leading-5 text-xs font-font1">Revision: 1</div>
            <div className="font-normal text-[#000000] leading-5 text-xs font-font1">Date: {moment().format("YYYY-MM-DD")}</div>
            <div className="font-normal text-[#000000] leading-5 text-xs font-font1">Document Number: 18-7-A</div>
          </div>
        </div>

        {showUserInfo ? (
          <>
            {/* Pre-step: User Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gray-900 text-white px-6 py-3 text-sm font-semibold tracking-wide text-left">
                USER INFORMATION
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-sm mb-4 text-left">Personal Details</h3>
                <div className="mb-4 flex flex-col">
                  <label className="text-sm font-medium mb-1 text-left">First Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.firstName} onChange={(e) => handleChange("firstName", e.target.value)} placeholder="First Name" className="border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="text-sm font-medium mb-1 text-left">Last Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.lastName} onChange={(e) => handleChange("lastName", e.target.value)} placeholder="Last Name" className="border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="text-sm font-medium mb-1 text-left">Phone Number <span className="text-red-500">*</span></label>
                  <div className="flex items-center gap-2">
                    <input
                      type="tel"
                      value={formData.phone}
                      maxLength={10}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        if (val.length <= 10) {
                          handleChange("phone", val);
                          setPhoneVerified(false);
                        }
                      }}
                      placeholder="Phone Number"
                      className="flex-1 border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                    />
                    {phoneVerified ? (
                      <span className="text-green-600 text-sm font-medium px-3 py-2 bg-green-50 rounded-md whitespace-nowrap">
                        Verified
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendPhoneOtp}
                        disabled={otpSending}
                        className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-gray-800 transition disabled:opacity-60 whitespace-nowrap"
                      >
                        {otpSending ? "Sending..." : "Validate"}
                      </button>
                    )}
                  </div>
                </div>
                <div className="mb-4 flex flex-col">
                  <label className="text-sm font-medium mb-1 text-left">Email Address <span className="text-red-500">*</span></label>
                  <input type="email" value={formData.emailAddress} onChange={(e) => handleChange("emailAddress", e.target.value)} placeholder="Email Address" className="border border-[#F3F3F5] bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400" />
                </div>
                <p className="text-xs text-gray-400 mt-4 text-left flex items-center gap-1">
                  <span className="inline-block w-4 h-4 rounded-full border border-gray-400 text-[10px] flex items-center justify-center">i</span>
                  All fields marked with <span className="text-red-500">*</span> are required
                </p>
              </div>
              <div className="flex justify-end px-6 py-4 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={handleUserInfoNext}
                  className="bg-gray-900 text-white px-6 py-2 rounded-md text-sm hover:bg-gray-800 flex items-center gap-1"
                >
                  Next &gt;
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
        {/* Stepper */}
        <div className="flex justify-center items-center gap-4 mb-8">
          {steps.map((step, index) => {
            const pageNum = index + 1;
            const isCompleted = pageNum < currentPage;
            const isCurrent = pageNum === currentPage;
            return (
              <div key={step} className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                    isCompleted
                      ? "bg-gray-800 text-white border-gray-800"
                      : isCurrent
                      ? "bg-gray-800 text-white border-gray-800"
                      : "bg-white text-gray-400 border-gray-300"
                  }`}
                >
                  {isCompleted ? <CheckIcon fontSize="small" /> : pageNum}
                </div>
                <span className={`text-[10px] mt-1 ${isCurrent ? "font-bold" : "text-gray-400"}`}>
                  {step}
                </span>
              </div>
            );
          })}
        </div>

        {/* Page Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Section Header */}
          <div className="bg-gray-900 text-white px-6 py-3 text-sm font-semibold tracking-wide text-left">
            {steps[currentPage - 1].toUpperCase()}
          </div>

          <div className="p-6">
            {/* PAGE 1: Company Info */}
            {currentPage === 1 && (
              <>
                <h3 className="font-semibold text-sm mb-4 text-left">Subcontractor Information</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="Subcontractor Name" value={formData.subcontractorName} onChange={(v) => handleChange("subcontractorName", v)} required />
                  <InputField label="Telephone Number" value={formData.telephoneNumber} onChange={(v) => handleChange("telephoneNumber", v)} required maxLength={15} digitsOnly />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="Street Address" value={formData.streetAddress} onChange={(v) => handleChange("streetAddress", v)} required />
                  <InputField label="Fax Number" value={formData.faxNumber} onChange={(v) => handleChange("faxNumber", v)} maxLength={15} digitsOnly />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="City" value={formData.city} onChange={(v) => handleChange("city", v)} required />
                  <InputField label="Website Address" value={formData.websiteAddress} onChange={(v) => handleChange("websiteAddress", v)} />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <InputField label="Province / State" value={formData.provinceState} onChange={(v) => handleChange("provinceState", v)} required />
                  <InputField label="Postal Code / Zip" value={formData.postalCode} onChange={(v) => handleChange("postalCode", v)} required />
                </div>

                <h3 className="font-semibold text-sm mb-4 text-left">Officers</h3>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <InputField label="President" value={formData.president} onChange={(v) => handleChange("president", v)} />
                  <InputField label="Vice President" value={formData.vicePresident} onChange={(v) => handleChange("vicePresident", v)} />
                  <InputField label="Treasurer" value={formData.treasurer} onChange={(v) => handleChange("treasurer", v)} />
                </div>

                <h3 className="font-semibold text-sm mb-3 text-left border-t border-gray-200 pt-2">Services provided to NTACT</h3>
                <textarea
                  value={formData.servicesProvided}
                  onChange={(e) => handleChange("servicesProvided", e.target.value)}
                  placeholder="Describe the services your organization will be providing..."
                  className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400 min-h-[80px]"
                />
              </>
            )}

            {/* PAGE 2: Contacts */}
            {currentPage === 2 && (
              <>
                <div className="mb-6">
                  <h4 className="font-semibold text-sm mb-3 text-left">4. Contact for Insurance Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Title" value={formData.contacts.insurance.title} onChange={(v) => handleContactChange("insurance", "title", v)} />
                    <InputField label="Telephone" value={formData.contacts.insurance.telephone} onChange={(v) => handleContactChange("insurance", "telephone", v)} maxLength={15} digitsOnly />
                    <InputField label="Fax" value={formData.contacts.insurance.fax} onChange={(v) => handleContactChange("insurance", "fax", v)} maxLength={15} digitsOnly />
                    <InputField label="Email" value={formData.contacts.insurance.email} onChange={(v) => handleContactChange("insurance", "email", v)} />
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="font-semibold text-sm mb-3 text-left">5. Contact for Requesting Bids</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Title" value={formData.contacts.requestingBids.title} onChange={(v) => handleContactChange("requestingBids", "title", v)} />
                    <InputField label="Telephone" value={formData.contacts.requestingBids.telephone} onChange={(v) => handleContactChange("requestingBids", "telephone", v)} maxLength={15} digitsOnly />
                    <InputField label="Fax" value={formData.contacts.requestingBids.fax} onChange={(v) => handleContactChange("requestingBids", "fax", v)} maxLength={15} digitsOnly />
                    <InputField label="Email" value={formData.contacts.requestingBids.email} onChange={(v) => handleContactChange("requestingBids", "email", v)} />
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="font-semibold text-sm mb-3 text-left">6. Contractor Evaluation Completed By</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField label="Title" value={formData.contacts.contractorEvaluation.title} onChange={(v) => handleContactChange("contractorEvaluation", "title", v)} />
                    <InputField label="Telephone" value={formData.contacts.contractorEvaluation.telephone} onChange={(v) => handleContactChange("contractorEvaluation", "telephone", v)} maxLength={15} digitsOnly />
                    <InputField label="Fax" value={formData.contacts.contractorEvaluation.fax} onChange={(v) => handleContactChange("contractorEvaluation", "fax", v)} maxLength={15} digitsOnly />
                    <InputField label="Email" value={formData.contacts.contractorEvaluation.email} onChange={(v) => handleContactChange("contractorEvaluation", "email", v)} />
                  </div>
                </div>
              </>
            )}

            {/* PAGE 3: HSE Info */}
            {currentPage === 3 && (
              <>
                <h3 className="font-semibold text-sm mb-4 text-left">Highest Ranking HSE Professional in the Firm</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="Name / Title" value={formData.hseNameTitle} onChange={(v) => handleChange("hseNameTitle", v)} />
                  <InputField label="Phone" value={formData.hsePhone} onChange={(v) => handleChange("hsePhone", v)} maxLength={15} digitsOnly />
                </div>
                <InputField label="Email" value={formData.hseEmail} onChange={(v) => handleChange("hseEmail", v)} />
              </>
            )}

            {/* PAGE 4: HSE Stats */}
            {currentPage === 4 && (
              <>
                <p className="text-sm text-gray-500 mb-4 text-left">
                  Enter your company's health and safety incident data for the previous three years.
                </p>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 text-sm font-semibold w-1/4">Health & Safety Incidents</th>
                      {years.map((y) => (
                        <th key={y} className="text-center py-2 px-3 text-sm font-semibold">{y}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "totalManhours", label: "Total Manhours" },
                      { key: "trir", label: "Total Recordable Incident Rate (TRIR)" },
                      { key: "lwc", label: "Lost Workday Cases (LWC)" },
                      { key: "lwdr", label: "Lost Workday Incident Rate (LWDR)" },
                    ].map((row) => (
                      <tr key={row.key} className="border-b">
                        <td className="py-3 px-3 text-sm font-medium text-left">{row.label}</td>
                        {years.map((y) => (
                          <td key={y} className="py-3 px-3">
                            <input
                              type="text"
                              value={formData.hseStats[row.key][y]}
                              onChange={(e) => handleStatChange(row.key, y, e.target.value)}
                              className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-center focus:outline-none focus:border-gray-400"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Additional Safety Metrics — scorecard inputs */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-1 text-left">Additional Safety Metrics</p>
                  <p className="text-xs text-gray-500 mb-3 text-left">Used to auto-calculate your safety scorecard.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-left block mb-1">EMR (Experience Modifier Rate)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.hseStats.emr || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, "");
                          setFormData({
                            ...formData,
                            hseStats: { ...formData.hseStats, emr: val },
                          });
                        }}
                        placeholder="e.g. 0.85"
                        className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-left block mb-1">Fatalities (Last 3 Years)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        value={formData.hseStats.fatalities_last_3_years || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          setFormData({
                            ...formData,
                            hseStats: { ...formData.hseStats, fatalities_last_3_years: val },
                          });
                        }}
                        placeholder="0"
                        className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-left block mb-1">Serious Incidents (Last 3 Years)</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        value={formData.hseStats.serious_incidents_last_3_years || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          setFormData({
                            ...formData,
                            hseStats: { ...formData.hseStats, serious_incidents_last_3_years: val },
                          });
                        }}
                        placeholder="0"
                        className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* PAGE 5: Safety */}
            {currentPage === 5 && (
              <div className="space-y-6">
                {/* Q1 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you have a company safety program?</p>
                      <YesNoRadio value={formData.safetyProgram} onChange={(v) => handleChange("safetyProgram", v)} noLabel="No (Please explain details in attachment)" />
                      {formData.safetyProgram === "No" && (
                        <UploadBox file={formData.safetyProgramAttachment} onChange={(f) => handleChange("safetyProgramAttachment", f)} onRemove={() => handleChange("safetyProgramAttachment", null)} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Q2 */}
                <div className="border-b border-gray-200  p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you have a safety manual?</p>
                      <YesNoRadio value={formData.safetyManual} onChange={(v) => handleChange("safetyManual", v)} noLabel="No (If yes, please attach a table of contents)" />
                      {formData.safetyManual === "Yes" && (
                        <UploadBox file={formData.safetyManualAttachment} onChange={(f) => handleChange("safetyManualAttachment", f)} onRemove={() => handleChange("safetyManualAttachment", null)} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Q3 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Does your company have a drug and alcohol testing program?</p>
                      <YesNoRadio value={formData.drugAlcoholTesting} onChange={(v) => handleChange("drugAlcoholTesting", v)} />
                    </div>
                  </div>
                </div>

                {/* Q4 - Online Databases */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">4</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-3 text-left">Are you currently subscribed to the following online safety databases?</p>
                      <div className="space-y-2 ml-2">
                        {Object.keys(formData.onlineDatabases).map((db) => (
                          <div key={db} className="flex items-center gap-4">
                            <span className="text-sm w-24 text-left">{db}:</span>
                            <YesNoRadio value={formData.onlineDatabases[db]} onChange={(v) => handleDatabaseChange(db, v)} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Q5 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">5</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Has your company ever been issued an OSHA Citation?</p>
                      <YesNoRadio value={formData.oshaCitation} onChange={(v) => handleChange("oshaCitation", v)} noLabel="No (If yes, please explain in an attachment)" />
                      {formData.oshaCitation === "Yes" && (
                        <UploadBox file={formData.oshaCitationAttachment} onChange={(f) => handleChange("oshaCitationAttachment", f)} onRemove={() => handleChange("oshaCitationAttachment", null)} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Q6 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">6</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you currently have a third-party medical consortium for case management of injuries? (i.e., Axiom, Extreme MD, Occurred)</p>
                      <YesNoRadio value={formData.thirdPartyMedical} onChange={(v) => handleChange("thirdPartyMedical", v)} />
                    </div>
                  </div>
                </div>

                {/* Q7 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">7</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you have a Job Safety Analysis (JSA) process in place?</p>
                      <YesNoRadio value={formData.jsaProcess} onChange={(v) => handleChange("jsaProcess", v)} />
                    </div>
                  </div>
                </div>

                {/* Q8 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">8</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Is there a Root Cause Analysis process used for investigations, near misses, and environmental spills?</p>
                      <YesNoRadio value={formData.rootCauseAnalysis} onChange={(v) => handleChange("rootCauseAnalysis", v)} />
                    </div>
                  </div>
                </div>

                {/* Q9 */}
                <div className="border-b border-gray-200 p-3">
                  <div className="flex items-start gap-3">
                    <span className="bg-gray-800 text-white rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0">9</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-left">Do you have an HSE Orientation Program for new hires and newly hired or promoted supervisors?</p>
                      <YesNoRadio value={formData.hseOrientation} onChange={(v) => handleChange("hseOrientation", v)} noLabel="No (If Yes, attach training curriculum)" />
                      {formData.hseOrientation === "Yes" && (
                        <UploadBox file={formData.hseOrientationAttachment} onChange={(f) => handleChange("hseOrientationAttachment", f)} onRemove={() => handleChange("hseOrientationAttachment", null)} />
                      )}
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* PAGE 6: Attestation */}
            {currentPage === 6 && (
              <>
                {/* Attestation Statement */}
                <div className="bg-[#FEF2F2] rounded-md p-4 mb-6">
                  <p className="text-sm text-gray-700 leading-relaxed text-left">
                    I attest that all information provided in previous pages is accurate according to the company's policies and procedures. The sub-contractor will comply with and adhere to all relevant Health, Safety, and Environmental standards set forth by NTACT and their clients.
                  </p>
                </div>

                {/* Print Name & Position */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <InputField label="Print Name" value={formData.printName} onChange={(v) => handleChange("printName", v)} required />
                  <InputField label="Position" value={formData.position} onChange={(v) => handleChange("position", v)} required />
                </div>

                {/* Signature & Date */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex flex-col flex-1">
                    <label className="text-sm font-medium mb-1 text-left">
                      Signature <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.signature}
                      onChange={(e) => handleChange("signature", e.target.value)}
                      placeholder="Type your full name to sign"
                      className="border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                    />
                  </div>
                  <div className="flex flex-col flex-1">
                    <label className="text-sm font-medium mb-1 text-left">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.attestationDate}
                      onChange={(e) => handleChange("attestationDate", e.target.value)}
                      className="border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
                    />
                  </div>
                </div>

                {/* Enclosed Documents */}
                <div>
                  <h3 className="font-semibold text-sm mb-2 text-left">Enclosed Documents</h3>
                  <p className="text-sm text-gray-500 mb-4 text-left">Check all documents that are included with this submission:</p>
                  <div className="space-y-3">
                    {["Employee Training Curriculum", "HSE Manual Table of Contents", "Employee Training Cards","Certificate Of Insurance Details","W9 Documents"].map((doc) => (
                      <label key={doc} className="flex items-center gap-3 border border-gray-200 rounded-md p-3 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={formData.enclosedDocuments.includes(doc)}
                          onChange={() => {
                            setFormData((prev) => ({
                              ...prev,
                              enclosedDocuments: prev.enclosedDocuments.includes(doc)
                                ? prev.enclosedDocuments.filter((d) => d !== doc)
                                : [...prev.enclosedDocuments, doc],
                            }));
                          }}
                          className="accent-black w-4 h-4"
                        />
                        <span className="text-sm">{doc}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1 && mode !== "new"}
            className="border border-gray-400 text-gray-600 px-8 py-2 rounded-md hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
          >
            &lt; Previous
          </button>
          <span className="text-sm text-gray-400 self-center">{currentPage} / {TOTAL_STEPS}</span>
          {currentPage < TOTAL_STEPS ? (
            <button
              onClick={goToNextPage}
              className="bg-gray-900 text-white px-8 py-2 rounded-md hover:bg-gray-800 flex items-center gap-1"
            >
              Next &gt;
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="bg-gray-900 text-white px-8 py-2 rounded-md hover:bg-gray-800 flex items-center gap-1"
            >
              <CheckIcon fontSize="small" /> Submit
            </button>
          )}
        </div>
          </>
        )}
      </div>

      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        handleClose={() => setToastOpen(false)}
      />

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Verify Phone Number</h3>
            <p className="text-sm text-gray-500 mb-5">
              Enter the 4-digit code sent to {formData.phone}
            </p>

            <div className="flex justify-center gap-3 mb-4">
              {otpDigits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (otpInputsRef.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleOtpDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpDigitKeyDown(i, e)}
                  className="w-12 h-14 text-center text-lg border border-gray-300 rounded-md focus:outline-none focus:border-gray-800"
                />
              ))}
            </div>

            <div className="text-center text-xs text-gray-500 mb-4">
              Didn't receive a code?{" "}
              <button
                type="button"
                onClick={() => {
                  if (otpResendTimer > 0) return;
                  handleSendPhoneOtp();
                }}
                disabled={otpResendTimer > 0}
                className={`underline ${otpResendTimer > 0 ? "text-gray-400 cursor-not-allowed" : "text-gray-700"}`}
              >
                Resend Code
                {otpResendTimer > 0 ? ` (0:${otpResendTimer.toString().padStart(2, "0")})` : ""}
              </button>
            </div>

            {otpError && (
              <div className="text-red-600 text-sm mb-3 text-center">{otpError}</div>
            )}

            <button
              onClick={handleVerifyPhoneOtp}
              disabled={loader}
              className="w-full bg-gray-900 text-white text-sm font-medium py-3 rounded-md hover:bg-gray-800 transition disabled:opacity-60"
            >
              Verify
            </button>
            <button
              onClick={() => {
                setShowOtpModal(false);
                setOtpDigits(["", "", "", ""]);
                setOtpError("");
              }}
              className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcontractorForm;
